import * as Sentry from "@sentry/react";
import { QuickNavigationOption, QuickNavigationUserOptionValue } from "config/quickNavigationOptions";
import { timeIncrement, zoomLevelDurationInHours } from "config/zoomConfig";
import UIStateContext from "contexts/UIStateContext";
import ApplicationContext from "domain/ApplicationContext";
import { useContext } from "react";
import DateTime from "utils/DateTime";
import useApplicationContext from "../apollo/applicationContext/useApplicationContext";

export default function useSchedulerState() {
  const { state, setState: updateUIState, getState } = useContext(UIStateContext);
  const applicationContext = useApplicationContext();

  const setState = (args) => {
    const newState = { ...state.scheduler, ...args };

    updateUIState({ scheduler: newState });
  };

  const getLatestState = () => {
    const latestState = getState().scheduler;

    return {
      ...latestState,
      extendedView: applicationContext.user?.userPreferences.extendedView,
      quickNavigationOption: normalizeQuickNavigationOption(latestState.quickNavigationOption),
    };
  };

  const updateStateAfter = (action) => (args) => {
    const currentState = getLatestState();
    const newState = action({ applicationContext, ...currentState, ...args });

    setState(newState);

    return newState;
  };

  return {
    changeDataTimeRange: updateStateAfter(doChangeDataTimeRange),
    changeVisibleStartDate: updateStateAfter(doChangeVisibleStartDate),
    changeVisibleTimeRange: updateStateAfter(doChangeVisibleTimeRange),
    changeZoomLevel: updateStateAfter(doChangeZoomLevel),
    extendedViewChanged: updateStateAfter(doExtendedViewChanged),
    getLatestState,
    quickNavigate: updateStateAfter(doMoveTimeRange),
    setState,
    state: getLatestState(),
    zoomIn: updateStateAfter(doZoomIn),
  };

  // Expects { extendedView: TRUE | FALSE }
  function doExtendedViewChanged(props) {
    const { extendedView, visibleTimeRange } = props;

    if (!extendedView) {
      return {
        ...props,
        dataTimeRange: {
          endDate: calculateEndDate({ startDate: new Date(visibleTimeRange.startDate), zoomLevel: props.zoomLevel }),
          startDate: new Date(visibleTimeRange.startDate),
        },
      };
    }

    return {
      ...props,
    };
  }

  // Expects { startDate: date, useStartOfDay: TRUE | FALSE }
  function doChangeVisibleStartDate(props) {
    const { startDate, zoomLevel, useStartOfDay = true } = props;

    const newStartDate = useStartOfDay
      ? DateTime.toISO8601(ApplicationContext.startOfWorkDay(applicationContext, startDate))
      : startDate;
    const hours = zoomLevelDurationInHours(zoomLevel);

    const newEndDate = DateTime.addDuration(newStartDate, hours, "hours");

    return {
      ...props,
      dataTimeRange: {
        endDate: calculateEndDate({ startDate: newStartDate, zoomLevel: props.zoomLevel }),
        startDate: new Date(newStartDate),
      },
      visibleTimeRange: { endDate: new Date(newEndDate), startDate: new Date(newStartDate) },
    };
  }

  // Expects { dataTimeRange: { startDate: date, endDate: date } }
  function doChangeDataTimeRange(props) {
    const {
      dataTimeRange: { startDate, endDate },
    } = props;

    return {
      ...props,
      dataTimeRange: {
        endDate: new Date(endDate),
        startDate: new Date(startDate),
      },
    };
  }

  // Expects { visibleTimeRange: { startDate: date, endDate: date } }
  function doChangeVisibleTimeRange(props) {
    return {
      ...props,
      visibleTimeRange: {
        endDate: new Date(props.visibleTimeRange.endDate),
        startDate: new Date(props.visibleTimeRange.startDate),
      },
    };
  }

  // Expects { zoomLevel: number }
  function doChangeZoomLevel(props) {
    const {
      zoomLevel,
      dataTimeRange: { startDate },
    } = props;

    const newStartDate = normalizeDate(startDate, zoomLevel);
    const newEndDate = calculateEndDate({ startDate: newStartDate, zoomLevel });

    return {
      ...props,
      dataTimeRange: {
        endDate: newEndDate,
        startDate: newStartDate,
      },
      zoomLevel,
    };
  }

  // Expects { quickNavigationOption: QuickNavigationOption }
  function doMoveTimeRange(props) {
    const { dataTimeRange, extendedView, quickNavigationOption, zoomLevel } = props;
    const {
      quickNavigationOption: currentQuickNavigationOption,
      visibleTimeRange: { startDate: currentVisibleStartDate },
    } = getLatestState();

    let newQuickNavigationOption = currentQuickNavigationOption;
    if (Object.values(QuickNavigationUserOptionValue).includes(quickNavigationOption)) {
      newQuickNavigationOption = quickNavigationOption;
    }

    const newStartDate = calculateStartDateToMoveTo({
      currentVisibleStartDate,
      quickNavigationOption,
      zoomLevel,
    });

    const newDataTimeRange = extendedView
      ? dataTimeRange
      : {
          endDate: calculateEndDate({ startDate: newStartDate, zoomLevel }),
          startDate: newStartDate,
        };

    return {
      ...props,
      dataTimeRange: newDataTimeRange,
      quickNavigationOption: newQuickNavigationOption,
      visibleTimeRange: {
        endDate: calculateVisibleEndDate({ startDate: newStartDate, zoomLevel }),
        startDate: newStartDate,
      },
    };
  }

  // Expects { startDate: date }
  function doZoomIn(props) {
    const { startDate, zoomLevel } = props;
    const newZoomLevel = zoomLevel - 1;

    const newStartDate = DateTime.toISO8601(ApplicationContext.startOfWorkDay(applicationContext, startDate));

    return {
      ...props,
      dataTimeRange: {
        endDate: calculateEndDate({ startDate: newStartDate, zoomLevel: newZoomLevel }),
        startDate: newStartDate,
      },
      visibleTimeRange: { endDate: new Date(newStartDate), startDate: new Date(newStartDate) },
      zoomLevel: newZoomLevel,
    };
  }

  function normalizeDate(returnDate, zoomLevel) {
    return DateTime.roundDownToNearestIncrement(returnDate, timeIncrement(zoomLevel));
  }

  // Expects { quickNavigationOption: QuickNavigationOption }
  function calculateStartDateToMoveTo({ currentVisibleStartDate, quickNavigationOption, zoomLevel }) {
    let returnDate;
    const hours = zoomLevelDurationInHours(zoomLevel);

    switch (quickNavigationOption) {
      case QuickNavigationOption.FORWARD:
        returnDate = new Date(DateTime.addDuration(currentVisibleStartDate, hours, "hours"));
        break;
      case QuickNavigationOption.BACK:
        returnDate = new Date(DateTime.subtractDuration(currentVisibleStartDate, hours, "hours"));
        break;
      case QuickNavigationOption.NOW:
        returnDate = new Date(DateTime.now());
        break;
      case QuickNavigationOption.WEEK_START:
        returnDate = new Date(ApplicationContext.startOfWorkWeek(applicationContext, DateTime.now()));
        break;
      case QuickNavigationOption.TODAY:
        returnDate = new Date(ApplicationContext.startOfWorkDay(applicationContext, DateTime.now()));
        break;
      default:
        Sentry.captureMessage(`Unexpected quickNavigationOption: ${quickNavigationOption}`, {});
        returnDate = new Date(DateTime.now());
    }

    return normalizeDate(returnDate, zoomLevel);
  }

  function calculateEndDate({ startDate }) {
    return new Date(DateTime.addDuration(startDate, "14", "days"));
  }

  function calculateVisibleEndDate({ startDate, zoomLevel }) {
    return new Date(DateTime.addDuration(startDate, zoomLevelDurationInHours(zoomLevel), "hours"));
  }

  function normalizeQuickNavigationOption(quickNavigationOption) {
    switch (quickNavigationOption) {
      case QuickNavigationUserOptionValue.WEEK_START:
      case QuickNavigationUserOptionValue.TODAY:
      case QuickNavigationUserOptionValue.NOW:
        return quickNavigationOption;
      default:
        return QuickNavigationUserOptionValue.TODAY;
    }
  }
}
